/**
 * Cross-origin iframe bridge transport (WI 4858 sub-plan 5, Tier U).
 *
 * The host-side counterpart to a `TENANT_APP` plugin rendered in a cross-origin
 * `<iframe sandbox>`. It brokers the SAME `ethisys:mcp:*` request/reply shapes
 * and host-side capability-token brokering as {@link WorkerRemoteDomTransport}
 * (the capability JWT NEVER crosses the boundary), but the transport is a
 * cross-origin iframe rather than a Worker, so it adds:
 *   - a per-mount handshake **nonce** delivered with the transferred MessagePort,
 *   - strict `targetOrigin` on every `postMessage` (never `"*"`),
 *   - Zod schema validation of every inbound envelope (unknown shapes dropped),
 *   - nonce validation on every inbound port message (defence-in-depth on top of
 *     the private MessageChannel + per-plugin origin boundary),
 *   - a rolling per-second inbound rate limit.
 *
 * MCP request handling (bounded concurrency, token brokering, reply shape) is
 * structurally identical to the worker transport.
 */
import { z } from "zod";
import { classifyHostError, classifyHostResponse } from "../../bridge/mcp-error";
import {
  type McpHttpClient,
  DEFAULT_MAX_CONCURRENT_MCP_REQUESTS,
} from "../worker/transport";
import {
  detectVersionSkew,
  type McpHttpResponse,
  type VersionSkew,
} from "../version-skew";

export const IFRAME_BRIDGE_PROTOCOL = "ethisys.iframe.bridge.v1";

/** Construction options for {@link IframeBridgeTransport}. */
export interface IframeBridgeTransportOptions {
  /** The cross-origin iframe's `contentWindow` (host never touches its document). */
  frameWindow: Window;
  /** EXACT plugin origin; every postMessage uses this as `targetOrigin`, never `"*"`. */
  targetOrigin: string;
  /**
   * Mints (or returns a cached) capability token. Resolved per outbound MCP HTTP
   * call on the HOST side only — the token NEVER crosses to the plugin frame.
   */
  capabilityToken: () => Promise<string>;
  /** Host-side MCP HTTP client; carries the capability token on the outbound call. */
  mcpClient: McpHttpClient;
  /**
   * The extension version this surface mounted at. Enables proactive skew detection — a successful
   * response stamped with a different version means the install moved while the surface is still
   * working, so it can remount before anything fails.
   */
  mountedExtensionVersion?: string;
  /**
   * Called at most once, when this surface is found to be running against a version that is no
   * longer installed. The host remounts the surface; the transport does not retry, because the
   * pending call belongs to a page about to be replaced.
   */
  onVersionSkew?: (skew: VersionSkew) => void;
  /** Bounded in-flight MCP requests before back-pressure. Default 8. */
  maxConcurrentMcpRequests?: number;
  /** Max inbound port messages per rolling second before drop. Default 64. */
  maxMessagesPerSecond?: number;
  /** Optional sink for dropped-message security telemetry (bad schema / nonce / rate). */
  onSecurityEvent?: (event: IframeBridgeSecurityEvent) => void;
}

/** Reason an inbound message was dropped. */
export type IframeBridgeSecurityEvent =
  | { reason: "schema-invalid" }
  | { reason: "nonce-mismatch" }
  | { reason: "rate-limit-exceeded"; type: string };

/** Handshake payload posted to the frame (carries the nonce + transferred port). */
export interface IframeHandshakePayload {
  readonly type: "ethisys:iframe:handshake";
  readonly protocol: string;
  readonly nonce: string;
}

// Inbound envelope schema — validated BEFORE any handling. Unknown shapes dropped.
const InboundEnvelope = z.discriminatedUnion("type", [
  z.object({ type: z.literal("ethisys:mcp:invokeTool"), id: z.string().min(1), name: z.string().min(1), args: z.unknown(), nonce: z.string() }),
  z.object({ type: z.literal("ethisys:mcp:getResource"), id: z.string().min(1), uri: z.string().min(1), nonce: z.string() }),
  z.object({ type: z.literal("ethisys:event"), name: z.string().min(1), payload: z.unknown(), nonce: z.string() }),
]);

type InboundMessage = z.infer<typeof InboundEnvelope>;

/** base64url-encode raw bytes (no padding). 32 bytes → 43 chars. */
function base64url(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) {
    binary += String.fromCharCode(b);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function mintNonce(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64url(bytes);
}

/**
 * Host-side cross-origin iframe bridge. Construct, then call {@link connect} to
 * post the handshake (transferring one end of a fresh `MessageChannel`). The
 * plugin frame must echo the handshake `nonce` on every port message.
 */
export class IframeBridgeTransport {
  private readonly frameWindowRef: { current: Window | null };
  private readonly targetOrigin: string;
  private readonly capabilityTokenProvider: () => Promise<string>;
  private readonly mcpClient: McpHttpClient;
  private readonly maxConcurrentMcpRequests: number;
  private readonly maxMessagesPerSecond: number;
  private readonly onSecurityEvent: ((event: IframeBridgeSecurityEvent) => void) | undefined;
  private readonly mountedExtensionVersion: string | undefined;
  private readonly onVersionSkew: ((skew: VersionSkew) => void) | undefined;
  private versionSkewReported = false;
  private readonly abortController = new AbortController();

  private hostPort: MessagePort | undefined;
  private framePort: MessagePort | undefined;
  private nonce: string | undefined;
  private eventConsumer: ((name: string, payload: unknown) => void) | undefined;
  private inFlightMcpRequests = 0;
  private windowTimestamps: number[] = [];
  private connected = false;
  private disposed = false;

  public constructor(options: IframeBridgeTransportOptions) {
    this.frameWindowRef = { current: options.frameWindow };
    this.targetOrigin = options.targetOrigin;
    this.capabilityTokenProvider = options.capabilityToken;
    this.mcpClient = options.mcpClient;
    this.mountedExtensionVersion = options.mountedExtensionVersion;
    this.onVersionSkew = options.onVersionSkew;
    this.maxConcurrentMcpRequests = Math.max(1, options.maxConcurrentMcpRequests ?? DEFAULT_MAX_CONCURRENT_MCP_REQUESTS);
    this.maxMessagesPerSecond = Math.max(1, options.maxMessagesPerSecond ?? 64);
    this.onSecurityEvent = options.onSecurityEvent;
  }

  /**
   * Post the handshake to the frame. Idempotent — only the FIRST call mints the
   * nonce, creates the channel, wires the host port, and transfers the frame
   * port. The handshake uses the exact `targetOrigin` (never `"*"`).
   */
  public connect(): void {
    if (this.connected || this.disposed) {
      return;
    }
    this.connected = true;

    this.nonce = mintNonce();
    const channel = new MessageChannel();
    this.hostPort = channel.port1;
    this.framePort = channel.port2;
    this.hostPort.onmessage = (ev) => this.handlePortMessage(ev.data);
    this.hostPort.start();

    const handshake: IframeHandshakePayload = {
      type: "ethisys:iframe:handshake",
      protocol: IFRAME_BRIDGE_PROTOCOL,
      nonce: this.nonce,
    };
    const frameWindow = this.frameWindowRef.current;
    frameWindow?.postMessage(handshake, this.targetOrigin, [this.framePort]);
  }

  /**
   * The per-mount handshake nonce (set after {@link connect}, `undefined` before
   * connect / after dispose). The host mount reads this to validate inbound
   * cross-origin `window`-level messages (which carry no MessagePort identity).
   */
  public get connectedNonce(): string | undefined {
    return this.nonce;
  }

  /** Register a consumer for inbound plugin→host `ethisys:event` envelopes. */
  public onEvent(consumer: (name: string, payload: unknown) => void): void {
    this.eventConsumer = consumer;
  }

  /**
   * Push a host→plugin envelope (theme/nav/chrome). Always uses the exact
   * `targetOrigin` (never `"*"`) and stamps the handshake nonce.
   */
  public postOutbound(envelope: Record<string, unknown>): void {
    if (this.disposed || this.nonce === undefined) {
      return;
    }
    const frameWindow = this.frameWindowRef.current;
    frameWindow?.postMessage({ ...envelope, nonce: this.nonce }, this.targetOrigin);
  }

  /** Tear down. Idempotent. Does NOT remove the iframe (the host mount owns it). */
  public dispose(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    try {
      this.abortController.abort();
    } catch {
      // best-effort
    }
    try {
      this.hostPort?.close();
    } catch {
      // ignore — may already be closed
    }
    this.nonce = undefined;
    this.frameWindowRef.current = null;
  }

  // ─── Inbound port message handling ────────────────────────────────────────

  private handlePortMessage(data: unknown): void {
    if (this.disposed) {
      return;
    }

    // 1. Schema validation — unknown shapes are dropped.
    const parsed = InboundEnvelope.safeParse(data);
    if (!parsed.success) {
      this.onSecurityEvent?.({ reason: "schema-invalid" });
      return;
    }
    const message = parsed.data;

    // 2. Nonce validation — defence-in-depth over the private MessageChannel.
    if (message.nonce !== this.nonce) {
      this.onSecurityEvent?.({ reason: "nonce-mismatch" });
      return;
    }

    // 3. Rolling per-second rate limit.
    if (this.isRateLimited()) {
      this.onSecurityEvent?.({ reason: "rate-limit-exceeded", type: message.type });
      if (message.type === "ethisys:mcp:invokeTool" || message.type === "ethisys:mcp:getResource") {
        this.safePostMessage({
          id: message.id,
          type: `${message.type}:result`,
          ok: false,
          error: "iframe-bridge rate limit exceeded",
        });
      }
      return;
    }

    // 4. Route.
    switch (message.type) {
    case "ethisys:mcp:invokeTool":
      this.dispatchMcp(message, "ethisys:mcp:invokeTool:result", (m) => this.handleInvokeTool(m));
      return;
    case "ethisys:mcp:getResource":
      this.dispatchMcp(message, "ethisys:mcp:getResource:result", (m) => this.handleGetResource(m));
      return;
    case "ethisys:event":
      this.eventConsumer?.(message.name, message.payload);
      return;
    }
  }

  private isRateLimited(): boolean {
    const now = Date.now();
    const cutoff = now - 1000;
    this.windowTimestamps = this.windowTimestamps.filter((t) => t > cutoff);
    if (this.windowTimestamps.length >= this.maxMessagesPerSecond) {
      return true;
    }
    this.windowTimestamps.push(now);
    return false;
  }

  // ─── MCP brokering (structurally identical to the worker transport) ───────

  private dispatchMcp<T extends { id: string }>(
    message: T,
    resultType: string,
    handler: (m: T) => Promise<void>,
  ): void {
    if (this.inFlightMcpRequests >= this.maxConcurrentMcpRequests) {
      this.safePostMessage({
        id: message.id,
        type: resultType,
        ok: false,
        error: `MCP back-pressure: in-flight cap of ${this.maxConcurrentMcpRequests} reached.`,
      });
      return;
    }
    this.inFlightMcpRequests++;
    handler(message).finally(() => {
      this.inFlightMcpRequests = Math.max(0, this.inFlightMcpRequests - 1);
    });
  }

  private async handleInvokeTool(message: Extract<InboundMessage, { type: "ethisys:mcp:invokeTool" }>): Promise<void> {
    let token: string;
    try {
      token = await this.capabilityTokenProvider();
    } catch (err) {
      this.replyError(message.id, "ethisys:mcp:invokeTool:result", err);
      return;
    }
    if (this.disposed) {
      return;
    }
    try {
      const result = await this.mcpClient.fetch({
        kind: "invokeTool",
        name: message.name,
        args: message.args,
        capabilityToken: token,
        signal: this.abortController.signal,
      });
      this.reportVersionSkew(result);
      // CRITICAL: the response payload must never include the token.
      this.safePostMessage({
        id: message.id,
        type: "ethisys:mcp:invokeTool:result",
        ok: result.ok,
        data: result.data,
        error: result.error,
        // A client that REPORTS failure rather than throwing lands here, which is the common shape
        // for anything wrapping HTTP. Classifying only the thrown path left these arriving as
        // `internal`.
        code: classifyHostResponse(result),
      });
    } catch (err) {
      this.replyError(message.id, "ethisys:mcp:invokeTool:result", err);
    }
  }

  /**
   * Raises the skew callback the first time this surface is seen to be running against a version
   * that is no longer installed. Latched: a page usually has several MCP calls in flight and after
   * an upgrade every one of them reports the same skew, so firing per response would ask the host
   * to remount the same surface repeatedly and each remount would tear down the last.
   *
   * The callback is host code and may throw. That must neither escape into the MCP handler's catch
   * (replying `ok: false` for a call that succeeded) nor consume the latch (leaving the surface with
   * no response and no further chance of being told to remount).
   */
  private reportVersionSkew(response: McpHttpResponse): void {
    if (this.versionSkewReported || this.onVersionSkew === undefined) {
      return;
    }
    const skew = detectVersionSkew(response, this.mountedExtensionVersion);
    if (skew === null) {
      return;
    }
    try {
      this.onVersionSkew(skew);
      this.versionSkewReported = true;
    } catch {
      // Swallowed on purpose: a failed remount attempt is the host's problem, and turning it into
      // an MCP failure would corrupt a response the plugin is entitled to. The latch stays open so
      // the next response gets another chance to raise it.
    }
  }

  private async handleGetResource(message: Extract<InboundMessage, { type: "ethisys:mcp:getResource" }>): Promise<void> {
    let token: string;
    try {
      token = await this.capabilityTokenProvider();
    } catch (err) {
      this.replyError(message.id, "ethisys:mcp:getResource:result", err);
      return;
    }
    if (this.disposed) {
      return;
    }
    try {
      const result = await this.mcpClient.fetch({
        kind: "getResource",
        uri: message.uri,
        capabilityToken: token,
        signal: this.abortController.signal,
      });
      this.reportVersionSkew(result);
      this.safePostMessage({
        id: message.id,
        type: "ethisys:mcp:getResource:result",
        ok: result.ok,
        data: result.data,
        error: result.error,
        // A client that REPORTS failure rather than throwing lands here, which is the common shape
        // for anything wrapping HTTP. Classifying only the thrown path left these arriving as
        // `internal`.
        code: classifyHostResponse(result),
      });
    } catch (err) {
      this.replyError(message.id, "ethisys:mcp:getResource:result", err);
    }
  }

  private replyError(id: string, type: string, err: unknown): void {
    const message = err instanceof Error ? err.message : "MCP request failed";
    // `code` accompanies the message so the plugin can branch on WHY the call failed. Without it a
    // plugin's only options were to match on host error prose or to swallow every failure
    // indiscriminately, and the latter is what shipped: permission denials and outages both became
    // "no results", which is a false statement about the user's data.
    this.safePostMessage({ id, type, ok: false, error: message, code: classifyHostError(err) });
  }

  /** Best-effort reply over the host port; tolerates post-dispose races. */
  private safePostMessage(message: unknown): void {
    if (this.disposed || this.hostPort === undefined) {
      return;
    }
    try {
      this.hostPort.postMessage(message);
    } catch {
      // Port may have closed between the disposed check and the post.
    }
  }
}
